import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Grid,
    Button,
    CircularProgress,
    Alert,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    FormHelperText,
    Autocomplete,
} from "@mui/material";
import {
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    PlayArrow as PlayArrowIcon,
    Code as CodeIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
} from "@mui/icons-material";
import AddAutomationPlaceholder from "../emailConfig/AddAutomationPlaceholder";
import JsonEditor from "../json-editor";
import Api from "../../services/api";
import { PapiroConsole } from "../../utils/papiroConsole";

const PAYLOAD_FORMATS = {
    CUSTOM_JSON: "CUSTOM_JSON",
    CUSTOM_FORM: "CUSTOM_FORM",
};

const defaultPayloadTemplate = `{}`;

const styles = {
    container: {
        padding: "16px",
        backgroundColor: "#fafafa",
        borderRadius: "8px",
        border: "1px solid #e0e0e0",
    },
    sectionTitle: {
        fontSize: "14px",
        fontWeight: 600,
        color: "#333",
        marginBottom: "12px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    editorContainer: {
        position: "relative",
        marginTop: "8px",
    },
    placeholderButtonContainer: {
        position: "absolute",
        top: "4px",
        right: "-42px",
        zIndex: 10,
    },
    previewContainer: {
        backgroundColor: "#f5f5f5",
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        padding: "12px",
        fontFamily: "monospace",
        fontSize: "12px",
        maxHeight: "200px",
        overflow: "auto",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
    },
    formDataRow: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "8px",
    },
    chipContainer: {
        display: "flex",
        flexWrap: "wrap",
        gap: "4px",
        marginTop: "8px",
    },
    authBadge: {
        fontSize: "10px",
        padding: "2px 6px",
        borderRadius: "3px",
        fontWeight: 500,
    },
};

export default function WebhookPayloadConfigAutomation({
    webhookId,
    payloadFormat,
    payloadTemplate,
    onWebhookChange,
    onPayloadFormatChange,
    onPayloadTemplateChange,
    onWebhookSelected = null, // Callback para passar o objeto webhook completo
    showChangedFields = true,
    disabled = false,
    error = null,
}) {
    const [webhooks, setWebhooks] = useState([]);
    const [loadingWebhooks, setLoadingWebhooks] = useState(false);
    const [selectedWebhook, setSelectedWebhook] = useState(null);
    const [preview, setPreview] = useState("");
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [formDataFields, setFormDataFields] = useState([{ key: "", value: "" }]);
    const [webhooksLoaded, setWebhooksLoaded] = useState(false);

    const textAreaRef = useRef(null);
    // Usar ref para evitar loop infinito com callback
    const onWebhookSelectedRef = useRef(onWebhookSelected);
    onWebhookSelectedRef.current = onWebhookSelected;

    // Carregar webhooks ativos - apenas uma vez
    useEffect(() => {
        if (webhooksLoaded) return;

        const loadWebhooks = async () => {
            setLoadingWebhooks(true);
            try {
                const response = await Api.get("/webhooks/active");
                if (response.data?.success && response.data?.data) {
                    setWebhooks(response.data.data);
                    setWebhooksLoaded(true);

                    // Se ja tem um webhookId selecionado, encontrar o webhook correspondente
                    if (webhookId) {
                        const found = response.data.data.find(w => w.id === webhookId);
                        if (found) {
                            setSelectedWebhook(found);
                            // Notificar o componente pai sobre o webhook encontrado
                            if (onWebhookSelectedRef.current) {
                                onWebhookSelectedRef.current(found);
                            }
                        }
                    }
                }
            } catch (err) {
                PapiroConsole.log("Erro ao carregar webhooks:", err);
            } finally {
                setLoadingWebhooks(false);
            }
        };

        loadWebhooks();
    }, [webhookId, webhooksLoaded]);

    // Inicializar payloadTemplate com valor padrao se estiver vazio
    useEffect(() => {
        if (!payloadTemplate && payloadFormat === PAYLOAD_FORMATS.CUSTOM_JSON) {
            onPayloadTemplateChange(defaultPayloadTemplate);
        }
    }, [payloadTemplate, payloadFormat, onPayloadTemplateChange]);

    // Sincronizar payloadTemplate com formDataFields quando formato muda para FORM
    useEffect(() => {
        if (payloadFormat === PAYLOAD_FORMATS.CUSTOM_FORM && payloadTemplate) {
            try {
                const parsed = JSON.parse(payloadTemplate);
                if (typeof parsed === "object" && !Array.isArray(parsed)) {
                    const fields = Object.entries(parsed).map(([key, value]) => ({
                        key,
                        value: String(value),
                    }));
                    if (fields.length > 0) {
                        setFormDataFields(fields);
                    }
                }
            } catch (e) {
                // Se nao for JSON valido, manter os campos atuais
            }
        }
    }, [payloadFormat]);

    // Handler para mudanca de webhook
    const handleWebhookChange = (event, newValue) => {
        setSelectedWebhook(newValue);
        onWebhookChange(newValue?.id || null);
        // Notificar o componente pai sobre o webhook completo selecionado (ou null se limpar)
        if (onWebhookSelectedRef.current) {
            onWebhookSelectedRef.current(newValue);
        }
    };

    // Handler para mudanca de formato de payload
    const handleFormatChange = (event) => {
        const newFormat = event.target.value;
        onPayloadFormatChange(newFormat);

        // Converter template se necessario
        if (newFormat === PAYLOAD_FORMATS.CUSTOM_FORM) {
            // Converter JSON para FormData fields
            try {
                const parsed = JSON.parse(payloadTemplate || "{}");
                const fields = Object.entries(parsed).map(([key, value]) => ({
                    key,
                    value: typeof value === "object" ? JSON.stringify(value) : String(value),
                }));
                setFormDataFields(fields.length > 0 ? fields : [{ key: "", value: "" }]);
            } catch (e) {
                setFormDataFields([{ key: "", value: "" }]);
            }
        } else if (newFormat === PAYLOAD_FORMATS.CUSTOM_JSON) {
            // Converter FormData fields para JSON
            const obj = {};
            formDataFields.forEach(field => {
                if (field.key.trim()) {
                    obj[field.key] = field.value;
                }
            });
            onPayloadTemplateChange(JSON.stringify(obj, null, 2));
        }
    };

    // Handler para mudanca de template JSON
    const handleTemplateChange = (newValue) => {
        onPayloadTemplateChange(newValue);
    };

    // Handler para mudanca de campos FormData
    const handleFormDataFieldChange = (index, field, value) => {
        const newFields = [...formDataFields];
        newFields[index][field] = value;
        setFormDataFields(newFields);

        // Atualizar template como JSON
        const obj = {};
        newFields.forEach(f => {
            if (f.key.trim()) {
                obj[f.key] = f.value;
            }
        });
        onPayloadTemplateChange(JSON.stringify(obj, null, 2));
    };

    // Adicionar novo campo FormData
    const handleAddFormDataField = () => {
        setFormDataFields([...formDataFields, { key: "", value: "" }]);
    };

    // Remover campo FormData
    const handleRemoveFormDataField = (index) => {
        if (formDataFields.length > 1) {
            const newFields = formDataFields.filter((_, i) => i !== index);
            setFormDataFields(newFields);

            const obj = {};
            newFields.forEach(f => {
                if (f.key.trim()) {
                    obj[f.key] = f.value;
                }
            });
            onPayloadTemplateChange(JSON.stringify(obj, null, 2));
        }
    };

    // Preview do payload
    const handlePreview = async () => {
        if (!payloadTemplate) {
            setPreviewError("Defina um template de payload primeiro");
            return;
        }

        // Validar sintaxe JSON antes de enviar (apenas para formato JSON)
        if (payloadFormat === PAYLOAD_FORMATS.CUSTOM_JSON) {
            try {
                JSON.parse(payloadTemplate);
            } catch (e) {
                setPreviewError("JSON inválido: " + e.message);
                return;
            }
        }

        setLoadingPreview(true);
        setPreviewError(null);

        try {
            const response = await Api.post("/automation/webhook/preview", {
                payloadTemplate: payloadTemplate,
                payloadFormat: payloadFormat,
                sampleCalledId: null, // Usar dados de exemplo
            });

            if (response.data?.success) {
                setPreview(response.data.data?.processedpayload || "");
            } else {
                setPreviewError(response.data?.errors?.join(", ") || "Erro ao gerar preview");
            }
        } catch (err) {
            PapiroConsole.log("Erro ao gerar preview:", err);
            setPreviewError("Erro ao gerar preview. Verifique a sintaxe do template.");
        } finally {
            setLoadingPreview(false);
        }
    };

    // Obter label do tipo de autenticacao
    const getAuthTypeLabel = (authType) => {
        const labels = {
            NONE: "Sem autenticacao",
            BASIC: "Basic Auth",
            BEARER: "Bearer Token",
            API_KEY: "API Key",
            OAUTH2: "OAuth 2.0",
        };
        return labels[authType] || authType;
    };

    // Obter cor do badge de autenticacao
    const getAuthTypeBadgeColor = (authType) => {
        const colors = {
            NONE: { bg: "#e0e0e0", color: "#666" },
            BASIC: { bg: "#bbdefb", color: "#1565c0" },
            BEARER: { bg: "#c8e6c9", color: "#2e7d32" },
            API_KEY: { bg: "#fff3e0", color: "#e65100" },
            OAUTH2: { bg: "#e1bee7", color: "#7b1fa2" },
        };
        return colors[authType] || { bg: "#e0e0e0", color: "#666" };
    };

    return (
        <Box sx={styles.container}>
            <Grid container spacing={2}>
                {/* Selecao de Webhook */}
                <Grid item xs={12}>
                    <Typography sx={styles.sectionTitle}>
                        <CodeIcon fontSize="small" />
                        Configuracao do Webhook
                    </Typography>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Autocomplete
                        options={webhooks}
                        value={selectedWebhook}
                        onChange={handleWebhookChange}
                        getOptionLabel={(option) => option.name || ""}
                        loading={loadingWebhooks}
                        disabled={disabled}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Selecione um Webhook"
                                placeholder="Buscar webhook..."
                                error={!!error}
                                helperText={error}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingWebhooks ? (
                                                <CircularProgress color="inherit" size={20} />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography variant="body1">{option.name}</Typography>
                                        <span
                                            style={{
                                                ...styles.authBadge,
                                                backgroundColor: getAuthTypeBadgeColor(option.authtype).bg,
                                                color: getAuthTypeBadgeColor(option.authtype).color,
                                            }}
                                        >
                                            {getAuthTypeLabel(option.authtype)}
                                        </span>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {option.endpointurl}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        noOptionsText="Nenhum webhook encontrado"
                        loadingText="Carregando webhooks..."
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormControl fullWidth disabled={disabled}>
                        <InputLabel>Formato do Payload</InputLabel>
                        <Select
                            value={payloadFormat || PAYLOAD_FORMATS.CUSTOM_JSON}
                            label="Formato do Payload"
                            onChange={handleFormatChange}
                        >
                            <MenuItem value={PAYLOAD_FORMATS.CUSTOM_JSON}>JSON</MenuItem>
                            <MenuItem value={PAYLOAD_FORMATS.CUSTOM_FORM}>Form Data</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {/* Info do Webhook Selecionado */}
                {selectedWebhook && (
                    <Grid item xs={12}>
                        <Alert severity="info" sx={{ py: 0.5 }}>
                            <Typography variant="body2">
                                <strong>URL:</strong> {selectedWebhook.endpointurl}
                                {selectedWebhook.httpmethod && (
                                    <Chip
                                        label={selectedWebhook.httpmethod}
                                        size="small"
                                        sx={{ ml: 1 }}
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                            </Typography>
                        </Alert>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                </Grid>

                {/* Editor de Payload - JSON */}
                {payloadFormat === PAYLOAD_FORMATS.CUSTOM_JSON && (
                    <Grid item xs={12}>
                        <Typography sx={{ ...styles.sectionTitle, fontSize: "13px" }}>
                            Template do Payload (JSON)
                        </Typography>
                        <Box sx={styles.editorContainer}>
                            <Box sx={styles.placeholderButtonContainer}>
                                <AddAutomationPlaceholder
                                    value={payloadTemplate || ""}
                                    onChange={handleTemplateChange}
                                    textAreaRef={textAreaRef}
                                    showChangedFields={showChangedFields}
                                />
                            </Box>
                            <JsonEditor
                                value={payloadTemplate || ""}
                                onChange={handleTemplateChange}
                                textareaRef={textAreaRef}
                                label="Template do Payload (JSON)"
                                placeholder="Digite o template JSON com variáveis {{ticket.id}}, {{ticket.title}}, etc."
                                minHeight={200}
                                maxHeight={500}
                            />
                            <FormHelperText>
                                Use variáveis como {`{{ticket.id}}`}, {`{{ticket.title}}`}, {`{{ticket.status.name}}`}, etc.
                                Clique no botão + para ver todas as variáveis disponíveis.
                            </FormHelperText>
                        </Box>
                    </Grid>
                )}

                {/* Editor de Payload - FormData */}
                {payloadFormat === PAYLOAD_FORMATS.CUSTOM_FORM && (
                    <Grid item xs={12}>
                        <Typography sx={{ ...styles.sectionTitle, fontSize: "13px" }}>
                            Campos do FormData
                        </Typography>
                        <Box>
                            {formDataFields.map((field, index) => (
                                <Box key={index} sx={styles.formDataRow}>
                                    <TextField
                                        size="small"
                                        label="Chave"
                                        value={field.key}
                                        onChange={(e) => handleFormDataFieldChange(index, "key", e.target.value)}
                                        disabled={disabled}
                                        sx={{ flex: 1 }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Valor"
                                        value={field.value}
                                        onChange={(e) => handleFormDataFieldChange(index, "value", e.target.value)}
                                        disabled={disabled}
                                        sx={{ flex: 2 }}
                                        placeholder="Ex: {{ticket.id}}"
                                    />
                                    <AddAutomationPlaceholder
                                        value={field.value}
                                        onChange={(newVal) => handleFormDataFieldChange(index, "value", newVal)}
                                        showChangedFields={showChangedFields}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveFormDataField(index)}
                                        disabled={disabled || formDataFields.length <= 1}
                                        color="error"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleAddFormDataField}
                                disabled={disabled}
                                sx={{ mt: 1 }}
                            >
                                Adicionar Campo
                            </Button>
                        </Box>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                </Grid>

                {/* Preview */}
                <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Typography sx={{ ...styles.sectionTitle, fontSize: "13px", mb: 0 }}>
                            <VisibilityIcon fontSize="small" />
                            Preview (com dados de exemplo)
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={loadingPreview ? <CircularProgress size={16} /> : <RefreshIcon />}
                            onClick={handlePreview}
                            disabled={loadingPreview || disabled || !payloadTemplate}
                        >
                            Gerar Preview
                        </Button>
                    </Box>

                    {previewError && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                            {previewError}
                        </Alert>
                    )}

                    {preview && (
                        <Box sx={styles.previewContainer}>
                            {preview}
                        </Box>
                    )}

                    {!preview && !previewError && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                            Clique em "Gerar Preview" para visualizar como o payload sera enviado.
                        </Typography>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}
